import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RefreshCw, Copy, Trash2, Check, Link2 } from 'lucide-react';
import { calendarAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const PLATFORMS = ['Airbnb', 'Booking.com', 'Vrbo', 'Other'];

/*
  Host-facing cross-platform calendar sync.
  - Export: copy Ariv's iCal link to paste into other platforms.
  - Import: paste other platforms' iCal links so Ariv blocks those dates.
  Self-contained; safe to drop anywhere a propertyId is in scope.
*/
const CalendarSync = ({ propertyId }) => {
  const { getAuthToken } = useAuth();
  const { toast } = useToast();
  const [exportUrl, setExportUrl] = useState('');
  const [imports, setImports] = useState([]);
  const [platform, setPlatform] = useState('Airbnb');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !propertyId) return;
    try {
      const [link, list] = await Promise.all([
        calendarAPI.getIcalLink(propertyId, token),
        calendarAPI.listIcalImports(propertyId, token),
      ]);
      setExportUrl(link?.exportUrl || '');
      setImports(list?.calendars || []);
    } catch (e) {
      // non-fatal — leave the panel empty
    }
  }, [propertyId, getAuthToken]);

  useEffect(() => { load(); }, [load]);

  const copyExport = async () => {
    try {
      await navigator.clipboard.writeText(exportUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast({ title: 'Could not copy', description: 'Select the link and copy it manually.', variant: 'destructive' });
    }
  };

  const add = async () => {
    const token = getAuthToken();
    if (!url.trim()) return;
    setBusy(true);
    try {
      const res = await calendarAPI.addIcalImport(propertyId, { platform, url: url.trim() }, token);
      setUrl('');
      toast({ title: 'Calendar connected', description: `Blocked ${res?.sync?.blocks ?? 0} dates from your calendars.` });
      await load();
    } catch (e) {
      toast({ title: 'Could not connect', description: e?.response?.data?.detail || 'Check the link and try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    const token = getAuthToken();
    setBusy(true);
    try {
      await calendarAPI.removeIcalImport(propertyId, u, token);
      await load();
    } catch (e) {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async () => {
    const token = getAuthToken();
    setBusy(true);
    try {
      const res = await calendarAPI.syncIcalNow(propertyId, token);
      toast({ title: 'Calendars synced', description: `${res?.blocks ?? 0} dates blocked from your other platforms.` });
      await load();
    } catch (e) {
      toast({ title: 'Sync failed', description: 'Please try again shortly.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 size={18} /> Calendar sync (Airbnb, Booking.com, Vrbo)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-gray-600">
          Keep your dates in step across platforms so you do not get a double booking. Sync runs automatically every couple of hours, so it is near real time, not instant.
        </p>

        {/* Export */}
        <div>
          <Label className="text-sm">Your Ariv calendar link</Label>
          <p className="text-xs text-gray-500 mb-2">Paste this into Airbnb / Booking.com / Vrbo so they block the dates booked on Ariv.</p>
          <div className="flex gap-2">
            <Input readOnly value={exportUrl} className="text-xs" />
            <Button type="button" variant="outline" onClick={copyExport} disabled={!exportUrl}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </Button>
          </div>
        </div>

        {/* Imports */}
        <div>
          <Label className="text-sm">Bring in your other calendars</Label>
          <p className="text-xs text-gray-500 mb-2">Paste the iCal link from Airbnb / Booking.com / Vrbo so Ariv blocks the dates they have booked.</p>
          {imports.length > 0 && (
            <div className="space-y-2 mb-3">
              {imports.map((c) => (
                <div key={c.url} className="flex items-center justify-between gap-2 text-sm border rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <p className="font-medium">{c.platform}</p>
                    <p className="text-xs text-gray-400 truncate">{c.url}</p>
                    {c.lastError ? (
                      <p className="text-xs text-red-600">Last sync failed</p>
                    ) : c.lastSyncedAt ? (
                      <p className="text-xs text-gray-400">{c.lastEvents ?? 0} dates synced</p>
                    ) : null}
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => remove(c.url)} disabled={busy}>
                    <Trash2 size={16} className="text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="border rounded-lg px-3 h-10 text-sm bg-white"
            >
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <Input
              placeholder="Paste iCal link (https://...)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="flex-1 text-sm"
            />
            <Button type="button" onClick={add} disabled={busy || !url.trim()} className="bg-[#1F3A68] hover:bg-[#1F3A68]/90">
              Connect
            </Button>
          </div>
        </div>

        <Button type="button" variant="outline" size="sm" onClick={syncNow} disabled={busy}>
          <RefreshCw size={14} className="mr-2" /> Sync now
        </Button>
      </CardContent>
    </Card>
  );
};

export default CalendarSync;
